import { UseQueryResult } from "@tanstack/react-query";
import React, { createContext, useContext } from "react";

import { GetReassessmentReportGoalsQuery, ReportGoalType } from "@api/graphql/types-and-hooks";
import { GoalsFilterValues } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalsFilterPopover";

export type SelectionState =
  | { mode: "none" }
  | { mode: "goal"; goalId: string }
  | { mode: "target"; targetId: string; goalId: string };

export type SelectionAction =
  | { type: "SELECT_GOAL"; goalId: string }
  | { type: "SELECT_TARGET"; targetId: string; goalId: string }
  | { type: "CLEAR" };

export interface ReportProgrammingContextI {
  reassessmentGoalsQuery: UseQueryResult<GetReassessmentReportGoalsQuery, unknown>;
  selected: SelectionState;
  dispatchSelected: React.Dispatch<SelectionAction>;
  isGasEnabled: boolean;
  filters: GoalsFilterValues;
  setFilters: React.Dispatch<React.SetStateAction<GoalsFilterValues>>;
  reassessmentGoalList: ReportGoalType[];
  // Unfiltered goal set (server response before the client-side date-range
  // filter). Used for selection lookups so a selected goal/target that's been
  // filtered out of the sidebar still resolves and renders in the editor pane.
  allGoals: ReportGoalType[];
}

export const ReportProgrammingContext = createContext<ReportProgrammingContextI | undefined>(undefined);

export const useReportProgrammingData = () => {
  const context = useContext(ReportProgrammingContext);
  if (context === undefined) {
    throw new Error("useReportProgrammingData must be used within a ReportProgrammingProvider");
  }
  return context;
};

export const selectionReducer = (state: SelectionState, action: SelectionAction): SelectionState => {
  switch (action.type) {
    case "SELECT_GOAL":
      return { mode: "goal", goalId: action.goalId };
    case "SELECT_TARGET":
      return { mode: "target", targetId: action.targetId, goalId: action.goalId };
    case "CLEAR":
      return { mode: "none" };
    default:
      return state;
  }
};
